import "swiper/css";
import { useEffect, useState, forwardRef } from 'react'
import dynamic from 'next/dynamic'
import dayjs from 'dayjs'
import { Swiper, SwiperSlide } from "swiper/react"
import ContentLoader from 'react-content-loader'

import { currencyFormat } from '@/lib/utils/format'
import { stockKline } from '@/lib/services/stock.service'

const Chart = dynamic(() => import('react-apexcharts'), { ssr: false })

const SuggestedInvestments = ({ assets = [], intersectEntry }, ref) => {
  const [klineDataList, setKlineDataList] = useState([])
  const [isVisible, setIsVisible] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const buildOptions = (code, data = []) => {
      const dates = data.map(d => d[0])
      const prices = data.map(d => d[1])
      const lastPrice = prices[prices.length-1]

      return {
        series: [{ name: code, data: prices }],
        chart: {
          id: `price-${code}`,
          type: 'area',
          height: 'auto',
          offsetY: 15,
          sparkline: { enabled: true },
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        dataLabels: { enabled: false },
        title: {
          text: code,
          align: 'left',
          margin: 0,
          offsetX: -10,
        },
        subtitle: {
          text: lastPrice ? currencyFormat(lastPrice) : '-',
          align: 'left',
          margin: 0,
          offsetX: -10,
          offsetY: 20,
        },
        labels: dates,
        colors: ['#FCB813'],
        fill: {
          type: "gradient",
        },
        stroke: {
          width: 1
        },
        xaxis: {
          show: false,
          crosshairs: {
            width: 1
          },
        },
        yaxis: {
          show: false,
        },
        legend: {
          horizontalAlign: 'left'
        },
        tooltip: {
          enabled: true,
          marker: {
            show: false,
          },
          x: {
            show: true,
            formatter: (value) => {
              return dayjs(value).format('DD MMM YYYY')
            }
          },
          y: {
            show: true,
            formatter: (value) => {
              return currencyFormat(value)
            }
          }
        }
      }
    }

    const fetchChartData = async () => {
      setLoading(true)
      for (const code of assets) {
        const data = await stockKline(code)
        const prices = Array.isArray(data)
          ? data.map(d => [dayjs(d?.bizDate).valueOf(), d?.closePrice])
          : []

        setKlineDataList(prev => (
          [...prev, {
            code,
            options: buildOptions(code, prices),
            series: [
              {
                name: `Close`,
                data: prices
              }
            ]
          }]
        ))
      }
      setLoading(false)
    }

    if (
      !isVisible &&
      intersectEntry?.isIntersecting
      && Array.isArray(assets)
      && assets.length > 0
    ) {
      setIsVisible(true)
      fetchChartData()
    }
  }, [assets, isVisible, intersectEntry?.isIntersecting])

  return (
    <>
      <div ref={ref} className="bg-white p-4 rounded-sm">
        {
          loading
            ? 
              <div className="flex justify-start relative h-4 mb-4 w-full">
                <ContentLoader
                  uniqueKey="si-title-loader"
                  speed={5}
                  viewBox="0 0 161 22"
                  backgroundColor="#CBCBCB"
                  foregroundColor="#DADADA"
                  style={{ width: 'auto', height: 'auto' }}
                >
                  <rect x="1" y="2" rx="2" ry="2" width="160" height="20px" />
                </ContentLoader>
              </div>
            :
              <h3 className="mb-4">Rekomendasi Aset</h3>
        }

        <Swiper
          spaceBetween={16}
          slidesPerView={3}
          speed={800}
          loop={true}
          breakpoints={{
            320: {
              slidesPerView: 1.3,
              spaceBetween: 16
            },
            480: {
              slidesPerView: 1.6,
              spaceBetween: 16
            },
            768: {
              slidesPerView: 2.3,
              spaceBetween: 16
            },
            1024: {
              slidesPerView: 3,
              spaceBetween: 16
            },
          }}
        >
          {
            loading
              ? 
                [1,2,3].map((i) => (
                  <SwiperSlide key={i}>
                    <div className="h-38 md:h-32 w-full overflow-hidden">
                      <ContentLoader
                        uniqueKey="si-swiper-loader"
                        speed={5}
                        viewBox="0 0 400 190"
                        backgroundColor="#CBCBCB"
                        foregroundColor="#DADADA"
                        style={{ width: '100%' }}
                      >
                        <rect x="5" y="20" rx="2" ry="2" width="100" height="25" /> 
                        <rect x="5" y="60" rx="2" ry="2" width="85" height="20" /> 
                        <circle cx="120" cy="170" r="12" />
                        <circle cx="164" cy="170" r="12" />
                        <circle cx="208" cy="170" r="12" />
                      </ContentLoader>
                    </div>
                  </SwiperSlide>
                ))
              :
                klineDataList &&
                klineDataList.length > 0 &&
                klineDataList.map((data, idx) => (
                  data?.options && 
                  data?.series && 
                  <SwiperSlide key={idx}>
                    <div className="h-38 md:h-32 w-full overflow-hidden">
                      <Chart
                        options={data.options}
                        series={data.series}
                        type="area"
                        width="100%"
                        height="auto"
                      />
                    </div>
                  </SwiperSlide>
                ))
          }
        </Swiper>
      </div>
    </>
  )
}



export default forwardRef(SuggestedInvestments)